<template lang="html">
  <Teleport to="body">
    <div
      v-show="isDesktop"
      class="catalog-mega-menu fixed inset-x-0 bottom-0 z-40 flex"
      :style="{ top: 'var(--app-header-height, var(--ui-header-height))' }"
    >
      <!-- Backdrop (below header); click closes -->
      <div
        class="catalog-mega-menu__backdrop absolute inset-0 bg-black/40"
        aria-hidden="true"
        @click="close()"
      />

      <div
        id="catalog-mega-menu-panel"
        ref="panelRef"
        role="dialog"
        aria-modal="true"
        aria-label="Каталог"
        class="catalog-mega-menu__panel relative z-10 h-full w-full overflow-hidden border-t border-default bg-default shadow-lg"
        @keydown="onPanelKeydown"
      >
        <!-- Content width matches header / page sections; white chrome stays full-bleed -->
        <div class="catalog-mega-menu__inner mx-auto flex h-full w-full max-w-(--ui-container)">
          <!-- Left: roots as links — hover/focus previews right panel; click navigates -->
          <nav
            ref="leftColRef"
            class="catalog-mega-menu__roots flex w-72 shrink-0 flex-col overflow-y-auto border-r border-default bg-default"
            aria-label="Корневые категории"
          >
            <template v-if="isLoading">
              <div
                v-for="n in 8"
                :key="`root-skel-${n}`"
                class="flex items-center gap-2.5 px-3 py-2"
              >
                <USkeleton class="size-7 shrink-0 rounded" />
                <USkeleton class="h-3.5 w-36" />
              </div>
            </template>

            <NuxtLink
              v-for="(root, index) in roots"
              :id="`catalog-mega-root-${root.id}`"
              :key="root.id"
              :to="rootTo(root)"
              class="catalog-mega-menu__root group flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-default transition-colors outline-none hover:bg-elevated focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset motion-reduce:transition-none"
              :class="{
                'bg-elevated font-medium text-primary': selectedRootId === root.id,
              }"
              :tabindex="selectedRootId === root.id || (!selectedRootId && index === 0) ? 0 : -1"
              :aria-current="selectedRootId === root.id ? 'true' : undefined"
              @mouseenter="onRootPointerEnter(root.id, $event)"
              @focus="selectRoot(root.id)"
              @keydown="onRootKeydown($event, index)"
              @click="onNavigate($event)"
            >
              <div
                class="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded bg-elevated"
                :class="{ 'bg-primary/10': selectedRootId === root.id }"
              >
                <UiPicture
                  v-if="root.images?.[0]"
                  class="size-7"
                  :src="root.images[0]"
                  variant="imageFull"
                  :alt="root.name"
                  sizes="28px"
                />
                <UIcon
                  v-else
                  name="i-lucide-layout-grid"
                  class="size-3.5"
                  :class="selectedRootId === root.id ? 'text-primary' : 'text-muted'"
                />
              </div>
              <span class="min-w-0 flex-1 truncate">
                {{ root.name }}
              </span>
              <UIcon
                v-if="rootHasChildren(root)"
                name="i-lucide-chevron-right"
                class="size-3.5 shrink-0"
                :class="selectedRootId === root.id ? 'text-primary' : 'text-muted opacity-50'"
              />
            </NuxtLink>

            <!-- P2 sale/promo footer slot (S8) — intentionally empty -->
            <div class="catalog-mega-menu__footer mt-auto border-t border-default" />
          </nav>

          <!-- Right: [1] group titles + [2] links (or leaf-[0] plaque) -->
          <div
            ref="rightColRef"
            class="catalog-mega-menu__panel-right min-w-0 flex-1 overflow-y-auto bg-default px-6 py-4 lg:px-8 lg:py-5"
            aria-live="polite"
          >
            <template v-if="isLoading">
              <div
                class="mb-5 space-y-2"
                aria-busy="true"
                aria-live="polite"
              >
                <USkeleton class="h-4 w-48" />
                <div class="grid grid-cols-2 gap-x-6 gap-y-1.5 lg:grid-cols-3 xl:grid-cols-4">
                  <USkeleton
                    v-for="n in 12"
                    :key="`link-skel-${n}`"
                    class="h-3.5 w-full"
                  />
                </div>
              </div>
              <div class="space-y-2">
                <USkeleton class="h-4 w-40" />
                <div class="grid grid-cols-2 gap-x-6 gap-y-1.5 lg:grid-cols-3 xl:grid-cols-4">
                  <USkeleton
                    v-for="n in 8"
                    :key="`link-skel-b-${n}`"
                    class="h-3.5 w-full"
                  />
                </div>
              </div>
            </template>

            <template v-else-if="groups.length">
              <div class="catalog-mega-menu__groups flex flex-col gap-5">
                <section
                  v-for="group in groups"
                  :key="group.key"
                  class="catalog-mega-menu__group"
                >
                  <NuxtLink
                    v-if="group.title"
                    :to="group.title.to"
                    class="mb-2 inline-flex max-w-full items-center gap-1 text-sm font-bold text-highlighted outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                    @click="onNavigate($event)"
                  >
                    <span class="truncate">
                      {{ group.title.label }}
                    </span>
                    <UIcon
                      v-if="group.title.showChevron"
                      name="i-lucide-chevron-right"
                      class="size-3.5 shrink-0 text-muted"
                    />
                  </NuxtLink>

                  <ul
                    v-if="group.links.length"
                    class="catalog-mega-menu__links grid list-none grid-cols-2 gap-x-6 gap-y-1 lg:grid-cols-3 xl:grid-cols-4"
                  >
                    <li
                      v-for="link in group.links"
                      :key="link.key"
                    >
                      <NuxtLink
                        :to="link.to"
                        class="inline-flex max-w-full items-center gap-0.5 rounded-sm py-0.5 text-sm leading-snug text-default outline-none hover:text-primary hover:underline focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                        @click="onNavigate($event)"
                      >
                        <span class="truncate">
                          {{ link.label }}
                        </span>
                        <UIcon
                          v-if="link.showChevron"
                          name="i-lucide-chevron-right"
                          class="size-3 shrink-0 text-muted"
                        />
                      </NuxtLink>
                    </li>
                  </ul>
                </section>
              </div>
            </template>

            <div
              v-else-if="leafRootPlaque"
              class="flex min-h-48 items-start justify-center px-2 pt-1 sm:px-4"
            >
              <NuxtLink
                :to="leafRootPlaque.to"
                class="catalog-mega-menu__leaf-plaque group relative flex w-full max-w-3xl items-center justify-between gap-6 overflow-hidden rounded-2xl bg-linear-to-r from-primary to-transparent px-7 py-9 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:gap-8 sm:px-10 sm:py-11"
                @click="onNavigate($event)"
              >
                <div class="relative z-10 flex min-w-0 flex-col items-start gap-4">
                  <p class="text-xl font-bold tracking-tight text-white sm:text-2xl">
                    {{ leafRootPlaque.name }}
                  </p>
                  <span
                    class="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-primary shadow-xs transition-transform group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                  >
                    {{ VIEW_ALL_LABEL }}
                  </span>
                </div>
                <UIcon
                  :name="brandIcons.logoLargeDark"
                  class="relative z-10 h-11 w-auto shrink-0 opacity-90 sm:h-14"
                  aria-hidden="true"
                />
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onKeyStroke, useScrollLock } from '@vueuse/core'

import { useDevice } from '#Common/composables/useDevice'
import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import { resolveCategoryDestination } from '#Products/support/categoryDestination'
import UiPicture from '~/app/components/Image/UiPicture.vue'
import { brandIcons } from '~/app/theme/brand'

const VIEW_ALL_LABEL = 'Посмотреть всё'

type MenuLink = {
  key: string
  label: string
  to: string
  showChevron: boolean
}

type MenuGroup = {
  key: string
  title: MenuLink | null
  links: MenuLink[]
}

const { isOpen, selectedRootId, close, selectRoot } = useCatalogMenu()
const { resolve } = useCategoryDestination()
const { store, support } = usePim()
const { isLgUp: isDesktop } = useDevice()
const isLoading = ref(false)

const panelRef = ref<HTMLElement | null>(null)
const leftColRef = ref<HTMLElement | null>(null)
const rightColRef = ref<HTMLElement | null>(null)

const bodyEl = ref<HTMLElement | null>(null)
const scrollLocked = useScrollLock(bodyEl)

const byIds = computed(() => store.categories?.allByIds ?? {})

const roots = computed(() => {
  const rootIds = store.categories?.rootIds ?? []
  return rootIds
    .map(id => byIds.value[id])
    .filter((c): c is CategoryDto => c != null)
    .toSorted((a, b) => a.order - b.order)
})

const selectedRoot = computed(() => {
  const id = selectedRootId.value
  if (id == null) return null
  return byIds.value[id] ?? null
})

/** Leaf [0]: right panel shows a CTA plaque instead of [1]/[2] groups. */
const leafRootPlaque = computed(() => {
  const root = selectedRoot.value
  if (!root || rootHasChildren(root)) return null
  return {
    name: root.name,
    to: rootTo(root),
  }
})

function rootHasChildren(category: CategoryDto): boolean {
  return (category.childrenIds ?? []).length > 0
}

function rootTo(category: CategoryDto): string {
  return resolve(category).route
}

function toMenuLink(category: CategoryDto): MenuLink {
  const { destination, route } = resolve(category)
  return {
    key: `cat-${category.id}`,
    label: category.name,
    to: route,
    showChevron: destination === 'hub',
  }
}

function toViewAllLink(category: CategoryDto): MenuLink {
  return {
    key: `view-all-${category.id}`,
    label: VIEW_ALL_LABEL,
    to: rootTo(category),
    showChevron: false,
  }
}

/**
 * Right panel anatomy: one group per [1] child of the selected [0].
 * Hub [1] → title + real [2] links. Leaf [1] → title + synthetic «Посмотреть всё».
 * Leaf [0] → empty groups; plaque rendered separately.
 */
const groups = computed<MenuGroup[]>(() => {
  const root = selectedRoot.value
  if (!root) return []

  const children = (root.childrenIds ?? [])
    .map(id => byIds.value[id])
    .filter((c): c is CategoryDto => c != null)
    .toSorted((a, b) => a.order - b.order)

  if (!children.length) return []

  return children.map((child) => {
    const destination = resolveCategoryDestination(child)

    if (destination === 'hub') {
      const deep = (child.childrenIds ?? [])
        .map(id => byIds.value[id])
        .filter((c): c is CategoryDto => c != null)
        .toSorted((a, b) => a.order - b.order)
        .map(toMenuLink)

      return {
        key: `hub-${child.id}`,
        title: toMenuLink(child),
        links: deep,
      }
    }

    return {
      key: `leaf-${child.id}`,
      title: toMenuLink(child),
      links: [toViewAllLink(child)],
    }
  })
})

/**
 * Close without restoring Catalog focus when navigation will not change
 * `route.fullPath` (already on that URL). Cross-route clicks rely on the
 * `useCatalogMenu` fullPath watch — closing here would unmount NuxtLink
 * before the router handles the activation.
 */
function onNavigate(event: MouseEvent) {
  const el = event.currentTarget as HTMLElement | null
  const href = el?.getAttribute('href')
  if (!href) return

  let pathname: string
  try {
    pathname = new URL(href, window.location.origin).pathname
  }
  catch {
    return
  }

  if (pathname === window.location.pathname) {
    nextTick(() => close({ restoreFocus: false }))
  }
}

async function ensureCategories() {
  if (store.categories) {
    ensureSelectedRoot()
    return
  }

  isLoading.value = true
  try {
    await support.loadCategories()
  }
  finally {
    isLoading.value = false
    ensureSelectedRoot()
  }
}

function ensureSelectedRoot() {
  if (selectedRootId.value != null) return
  const first = roots.value[0]
  if (first) {
    selectRoot(first.id)
  }
}

function onRootPointerEnter(id: number, event: MouseEvent) {
  selectRoot(id)

  const target = event.currentTarget as HTMLElement | null
  const active = document.activeElement
  if (
    target
    && active
    && leftColRef.value?.contains(active)
  ) {
    target.focus({ preventScroll: true })
  }
}

function focusRootAt(index: number) {
  const root = roots.value[index]
  if (!root) return
  selectRoot(root.id)
  nextTick(() => {
    document.getElementById(`catalog-mega-root-${root.id}`)?.focus()
  })
}

function onRootKeydown(event: KeyboardEvent, index: number) {
  const count = roots.value.length
  if (!count) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      focusRootAt((index + 1) % count)
      break
    case 'ArrowUp':
      event.preventDefault()
      focusRootAt((index - 1 + count) % count)
      break
    case 'ArrowRight':
      event.preventDefault()
      focusFirstRightLink()
      break
    case 'Home':
      event.preventDefault()
      focusRootAt(0)
      break
    case 'End':
      event.preventDefault()
      focusRootAt(count - 1)
      break
  }
}

function focusableIn(container: HTMLElement | null) {
  if (!container) return []
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1)
}

function focusFirstRightLink() {
  const links = rightColRef.value?.querySelectorAll<HTMLElement>('a[href]')
  const first = links?.[0]
  first?.focus()
}

function onPanelKeydown(event: KeyboardEvent) {
  const active = document.activeElement as HTMLElement | null

  if (
    event.key === 'ArrowLeft'
    && active
    && rightColRef.value?.contains(active)
  ) {
    event.preventDefault()
    const selected = selectedRootId.value
    if (selected != null) {
      document.getElementById(`catalog-mega-root-${selected}`)?.focus()
    }
    return
  }

  if (event.key !== 'Tab' || !panelRef.value) return

  const focusables = [
    ...focusableIn(leftColRef.value),
    ...focusableIn(rightColRef.value),
  ]
  if (!focusables.length) return

  const first = focusables[0]!
  const last = focusables[focusables.length - 1]!

  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  }
  else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

onKeyStroke('Escape', (event) => {
  if (!isOpen.value) return
  event.preventDefault()
  close()
})

watch(isDesktop, (desktop) => {
  if (!desktop) {
    close()
  }
})

onMounted(async () => {
  bodyEl.value = document.body
  scrollLocked.value = true

  await ensureCategories()
  nextTick(() => {
    const selected = selectedRootId.value
    const el = selected
      ? document.getElementById(`catalog-mega-root-${selected}`)
      : leftColRef.value?.querySelector<HTMLElement>('a[href]')
    el?.focus()
  })
})

onUnmounted(() => {
  scrollLocked.value = false
})
</script>
